import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import mapboxgl from 'mapbox-gl';
import Point from '@mapbox/point-geometry';
import { isEqual } from 'lodash-es';

import clsx from '../clsx';
import colors from '../colors';
import useMap from './useMap';
import { latLngToLngLat } from './utils';

function Marker(props) {
  const {
    className: classNameProp,
    latLng,
    children,
    ...mapboxMarkerOptions
  } = props;

  const map = useMap();

  const prevPropsRef = useRef(props);

  // Mapbox marker instance
  const marker = useMemo(
    () => {
      const hasChildren = React.Children.toArray(children).length > 0;

      const markerInstance = new mapboxgl.Marker({
        ...mapboxMarkerOptions,
        element: hasChildren ? document.createElement('div') : undefined
      });

      markerInstance.setLngLat(latLngToLngLat(latLng));

      return markerInstance;
    },
    // As we only want to create mapboxgl.Marker once and as we manually update it's props/options,
    // we intentionally have empty dependency array and disabled the eslint rule here.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(
    () => {
      const handleDragStart = (e) => {
        e.lngLat = marker.getLngLat();
        prevPropsRef.current.onDragStart(e);
      };
      const handleDrag = (e) => {
        e.lngLat = marker.getLngLat();
        prevPropsRef.current.onDrag(e);
      };
      const handleDragEnd = (e) => {
        e.lngLat = marker.getLngLat();
        prevPropsRef.current.onDragEnd(e);
      };

      marker.on('dragstart', handleDragStart);
      marker.on('drag', handleDrag);
      marker.on('dragend', handleDragEnd);
      marker.addTo(map);

      return () => {
        marker.off('dragstart', handleDragStart);
        marker.off('drag', handleDrag);
        marker.off('dragend', handleDragEnd);
        marker.remove();
      };
    },
    // We only want to attach events once on initial render and as marker will not change in the
    // lifespan of this component we intentionally have empty dependency array and disabled the
    // eslint rule here.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  //
  // Handle updating class list
  //
  // Filter out any mapboxgl specific class names as we don not want to update them
  const filteredClassNames = [...marker.getElement().classList].filter(
    (className) => !/mapboxgl-/.test(className)
  );
  const prevClassNameString = clsx(filteredClassNames);
  const nextClassNameString = clsx(classNameProp);

  if (prevClassNameString !== nextClassNameString) {
    const prevClassNames = prevClassNameString.split(/\s+/);
    const newClassNames = nextClassNameString.split(/\s+/);

    prevClassNames.forEach((className) => {
      if (className && !newClassNames.includes(className)) {
        marker.getElement().classList.remove(className);
      }
    });

    newClassNames.forEach((className) => {
      if (className && !prevClassNames.includes(className)) {
        marker.getElement().classList.add(className);
      }
    });
  }

  //
  // Handle updating position
  //
  const [prevLat, prevLng] = prevPropsRef.current.latLng;
  const [lat, lng] = latLng;
  if (prevLat !== lat || prevLng !== lng) {
    marker.setLngLat([lng, lat]);
  }

  //
  // Update the offset property
  //
  if (
    mapboxMarkerOptions.offset &&
    !isEqual(marker.getOffset(), Point.convert(mapboxMarkerOptions.offset))
  ) {
    marker.setOffset(mapboxMarkerOptions.offset);
  }

  //
  // Update the draggable property
  //
  if (marker.isDraggable() !== mapboxMarkerOptions.draggable) {
    marker.setDraggable(mapboxMarkerOptions.draggable);
  }

  //
  // Update the rotation property
  //
  if (marker.getRotation() !== mapboxMarkerOptions.rotation) {
    marker.setRotation(mapboxMarkerOptions.rotation);
  }

  //
  // Update the rotationAlignment property
  //
  if (marker.getRotationAlignment() !== mapboxMarkerOptions.rotationAlignment) {
    marker.setRotationAlignment(mapboxMarkerOptions.rotationAlignment);
  }

  //
  // Update the pitchAlignment property
  //
  if (marker.getPitchAlignment() !== mapboxMarkerOptions.pitchAlignment) {
    marker.setPitchAlignment(mapboxMarkerOptions.pitchAlignment);
  }

  // This needs to be at the bottom so that we make sure prevProps gets updates after all the checks
  // and updates have been performed.
  prevPropsRef.current = props;

  return createPortal(children, marker.getElement());
}

Marker.propTypes = {
  /**
   * A string indicating the part of the popup that should be positioned closest to the coordinate.
   * Options are 'center', 'top', 'bottom', 'left', 'right', 'top-left', 'top-right',
   * 'bottom-left', and 'bottom-right'. If unset, the anchor will be dynamically set to ensure the
   * popup falls within the map container with a preference for 'bottom'.
   */
  anchor: PropTypes.oneOf([
    'center',
    'top',
    'bottom',
    'left',
    'right',
    'top-left',
    'top-right',
    'bottom-left',
    'bottom-right'
  ]),

  /**
   * The max number of pixels a user can shift the mouse pointer during a click on the marker for it
   * to be considered a valid click (as opposed to a marker drag). The default is to inherit map's
   * `clickTolerance`.
   */
  clickTolerance: PropTypes.number,

  /**
   * A boolean indicating whether or not a marker is able to be dragged to a new position on the map.
   */
  draggable: PropTypes.bool,

  /**
   * The offset in pixels as a [PointLike](https://docs.mapbox.com/mapbox-gl-js/api/geography/#pointlike)
   * object to apply relative to the element's center. Negatives indicate left and up.
   */
  offset(props, propName, componentName) {
    const isPoint = props[propName] instanceof mapboxgl.Point;
    const isTwoNumberArray =
      Array.isArray(props[propName]) &&
      props[propName].length === 2 &&
      props[propName].every((v) => typeof v === 'number');

    if (props[propName] && !(isPoint || isTwoNumberArray)) {
      return new Error(
        `Invalid prop \`${propName}\` supplied to \`${componentName}\`, ` +
          `expected a mapboxgl.Point or an array of two numbers representing x and y screen coordinates in pixels, ` +
          `but got \`${props[propName]}\`.`
      );
    }

    return null;
  },

  /**
   * The rotation angle of the marker in degrees, relative to its respective `rotationAlignment`
   * setting. A positive value will rotate the marker clockwise.
   */
  rotation: PropTypes.number,

  /**
   * - `map` aligns the Marker to the plane of the map
   * - `viewport` aligns the Marker to the plane of the viewport
   * - `auto` automatically matches the value of rotationAlignment.
   */
  pitchAlignment: PropTypes.oneOf(['map', 'viewport', 'auto']),

  /**
   * - `map` aligns the Marker's rotation relative to the map, maintaining a bearing as the map rotates
   * - `viewport` aligns the Marker's rotation relative to the viewport, agnostic to map rotations
   * - `auto` is equivalent to viewport
   */
  rotationAlignment: PropTypes.oneOf(['map', 'viewport', 'auto']),

  /**
   * The color to use for the default marker if `children` are not provided. The default is light blue.
   */
  color: PropTypes.string,

  /**
   * The scale to use for the default marker if `children` are not provided.
   * The default scale corresponds to a height of `41px` and a width of `27px`.
   */
  scale: PropTypes.number,

  /**
   * Called when dragging starts
   */
  onDragStart: PropTypes.func,

  /**
   * Called while dragging
   */
  onDrag: PropTypes.func,

  /**
   * Called when the marker is finished being dragged
   */
  onDragEnd: PropTypes.func,

  //
  // Custom props that are not natively supported by mapbox-gl-js
  //

  /**
   * Array of two numbers representing latitude and longitude
   */
  latLng(props, propName, componentName) {
    const isTwoNumberArray =
      Array.isArray(props[propName]) &&
      props[propName].length === 2 &&
      props[propName].every((v) => typeof v === 'number');

    if (props[propName] && !isTwoNumberArray) {
      return new Error(
        `Invalid prop \`${propName}\` supplied to \`${componentName}\`, ` +
          `expected an array of two numbers [latitude, longitude], ` +
          `but got \`${props[propName]}\`.`
      );
    }

    return null;
  },

  /**
   * CSS class names to add to marker container
   */
  className: PropTypes.string,

  children: PropTypes.node
};

Marker.defaultProps = {
  anchor: 'center',
  clickTolerance: 0,
  draggable: false,
  rotation: 0,
  pitchAlignment: 'auto',
  rotationAlignment: 'auto',
  color: colors.O50_STRAVA_ORANGE,
  scale: 1,
  // Events
  onDragStart: () => {},
  onDrag: () => {},
  onDragEnd: () => {},
  // Custom props that are not natively supported by mapbox-gl-js
  className: null,
  children: null
};

export default Marker;
